import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Maximize2, Expand, Shrink } from "lucide-react";

const ACCENTS = {
  teal: "border-l-[hsl(var(--primary))]",
  emerald: "border-l-emerald-500",
  amber: "border-l-amber-500",
  slate: "border-l-slate-400",
  sky: "border-l-sky-500",
};

export const KpiCard = ({ label, value, sub, delta, accent = "teal", testId }) => {
  const deltaColor = delta == null ? "" : delta >= 0
    ? "text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50"
    : "text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-950/50";
  return (
    <Card data-testid={testId}
      className={`rounded-xl border bg-card shadow-[var(--app-shadow-sm)] p-4 md:p-5 border-l-[3px] ${ACCENTS[accent]} hover:shadow-[var(--app-shadow)] transition-[box-shadow] duration-200`}>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      <div className="flex items-end justify-between mt-2 gap-2">
        <p className="text-2xl md:text-3xl font-bold text-foreground tracking-tight tabular-nums" style={{ fontFamily: "Space Grotesk" }}>
          {value}
        </p>
        {delta != null && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${deltaColor} tabular-nums`}>
            {delta >= 0 ? "+" : ""}{delta}%
          </span>
        )}
      </div>
      {sub && <p className="text-xs text-muted-foreground/70 mt-1">{sub}</p>}
    </Card>
  );
};

export const ChartCard = ({ title, subtitle, testId, className = "", render, isExpanded, onToggleSize }) => {
  const [maximized, setMaximized] = useState(false);
  return (
    <>
      <Card data-testid={testId} className={`rounded-xl border bg-card shadow-[var(--app-shadow-sm)] p-4 md:p-5 ${className}`}>
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex gap-0.5 shrink-0">
            {onToggleSize && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground/70 hover:text-foreground"
                data-testid={`${testId}-size-toggle`}
                aria-label={isExpanded ? "Shrink card" : "Expand card"}
                title={isExpanded ? "Shrink to default width" : "Expand to full width"}
                onClick={onToggleSize}>
                {isExpanded ? <Shrink className="h-3.5 w-3.5" /> : <Expand className="h-3.5 w-3.5" />}
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground/70 hover:text-foreground"
              data-testid={`${testId}-maximize`}
              aria-label="Maximize chart" title="Maximize"
              onClick={() => setMaximized(true)}>
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {render(isExpanded ? 340 : 280)}
      </Card>

      <Dialog open={maximized} onOpenChange={setMaximized}>
        <DialogContent className="max-w-6xl w-[92vw]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
          </DialogHeader>
          <div className="pt-2">{render(Math.max(420, Math.round(window.innerHeight * 0.6)))}</div>
        </DialogContent>
      </Dialog>
    </>
  );
};
